import type { ScoreBreakdown } from '../../recommendations/types';
import type { MatchBreakdown, MatchProfileContext } from '../components/JobMatchExplainer';

function pct(n: unknown): number {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round(Math.min(100, Math.max(0, v <= 1 ? v * 100 : v)));
}

function deriveFactor(base: number, offset: number): number {
  return Math.round(Math.min(100, Math.max(0, base + offset)));
}

function mapFactors(overall: number, b?: ScoreBreakdown | null): MatchBreakdown['factors'] {
  if (b) {
    const experience = pct(b.experience_alignment);
    const skills = pct(b.skill_overlap);
    const industry = pct(b.semantic_similarity);
    const location = pct(b.location_fit);
    const seniority = Math.round((pct(b.budget_fit) + pct(b.deadline_feasibility)) / 2);
    return { experience, skills, industry, location, seniority };
  }

  const o = pct(overall);
  return {
    experience: deriveFactor(o, 4),
    skills: deriveFactor(o, 2),
    industry: deriveFactor(o, -3),
    location: deriveFactor(o, 8),
    seniority: deriveFactor(o, 1),
  };
}

/** Build human-readable "strong match" bullets from profile + skills. */
export function buildStrongMatchReasons(
  matchedSkills: string[],
  ctx?: MatchProfileContext | null,
  summary?: string,
): string[] {
  const reasons: string[] = [];

  const years = ctx?.experienceYears;
  if (years != null && years > 0) {
    reasons.push(`__experience__:${years}`);
  }

  for (const skill of matchedSkills) {
    const s = skill.trim();
    if (!s || reasons.length >= 6) break;
    if (!reasons.some((r) => r.endsWith(s))) reasons.push(s);
  }

  if (ctx?.industry?.trim() && reasons.length < 6) {
    reasons.push(`__industry__:${ctx.industry.trim()}`);
  }

  const role = ctx?.currentRole?.trim() ?? '';
  if (role && /lead|manager|head|director|principal|staff/i.test(role) && reasons.length < 6) {
    reasons.push('__leadership__');
  }

  if (reasons.length < 3 && summary?.trim()) {
    summary
      .split(/[.;]\s+/)
      .map((p) => p.trim())
      .filter((p) => p.length > 8 && p.length < 90)
      .slice(0, 2)
      .forEach((p) => {
        if (reasons.length < 6) reasons.push(p);
      });
  }

  return reasons.slice(0, 6);
}

export function mapScoreToBreakdown(input: {
  overall: number;
  breakdown?: ScoreBreakdown | null;
  matchedSkills?: string[];
  missingSkills?: string[];
  summary?: string;
  profile?: MatchProfileContext | null;
}): MatchBreakdown {
  const matched = input.matchedSkills ?? [];
  const missing = input.missingSkills ?? [];

  return {
    overall: pct(input.overall),
    factors: mapFactors(input.overall, input.breakdown),
    matchedSkills: matched,
    missingSkills: missing,
    strongReasons: buildStrongMatchReasons(matched, input.profile, input.summary),
    summary: input.summary,
  };
}
